'use client';

import React, { useState, useEffect } from 'react';
import Image from 'next/image';
import { motion } from 'framer-motion';
import {
    ShoppingCart, Cpu, GraduationCap, Utensils, Stethoscope, Building2,
    Palette, Scale, Search, Layout, TrendingUp, Zap, ArrowRight, CheckCircle2, Sparkles
} from 'lucide-react';
import { useContactModal } from '../../../contexts/ContactModalContext';

function useOrbitSize() {
    const [size, setSize] = useState({ rx: 270, ry: 230, minH: 560, offsetY: -60 });
    useEffect(() => {
        function update() {
            const w = window.innerWidth;
            if (w < 400) {
                setSize({ rx: 115, ry: 98, minH: 340, offsetY: -25 });
            } else if (w < 480) {
                setSize({ rx: 130, ry: 112, minH: 360, offsetY: -30 });
            } else if (w < 640) {
                setSize({ rx: 165, ry: 142, minH: 420, offsetY: -40 });
            } else if (w < 768) {
                setSize({ rx: 195, ry: 165, minH: 460, offsetY: -45 });
            } else {
                setSize({ rx: 270, ry: 230, minH: 560, offsetY: -60 });
            }
        }
        update();
        window.addEventListener('resize', update);
        return () => window.removeEventListener('resize', update);
    }, []);
    return size;
}

// ─── Industries Hero ─────────────────────────────────────────────────────────

const ORBIT_INDUSTRIES = [
    { icon: ShoppingCart, title: "E-Commerce", color: "#3b82f6" },
    { icon: GraduationCap, title: "Education", color: "#06b6d4" },
    { icon: Stethoscope, title: "Health Care", color: "#10b981" },
    { icon: Utensils, title: "Restaurant", color: "#f97316" },
    { icon: Building2, title: "Real Estate", color: "#f59e0b" },
    { icon: Cpu, title: "SaaS", color: "#6366f1" },
    { icon: Palette, title: "Interior Design", color: "#ec4899" },
    { icon: Scale, title: "Law Firm", color: "#64748b" },
];

// Rounded to avoid server/client floating-point mismatches (hydration errors) in Math.cos/sin output.
const pt = (angleDeg: number, rx: number, ry: number) => {
    const r = (angleDeg * Math.PI) / 180;
    return { x: Math.round(Math.cos(r) * rx * 1000) / 1000, y: Math.round(Math.sin(r) * ry * 1000) / 1000 };
};

const orbitKeyframes = (startAngle: number, rx: number, ry: number, axis: 'x' | 'y') =>
    Array.from({ length: 361 }, (_, i) => pt(startAngle + i, rx, ry)[axis]);

function IndustriesHero() {
    const { rx: RX, ry: RY, minH, offsetY: OFFSET_Y } = useOrbitSize();
    const DUR = 30;

    return (
        <section className="relative pt-40 pb-24 px-6 md:px-24 overflow-hidden bg-white" data-theme="light">
            <div className="absolute top-0 right-0 w-[800px] h-[800px] rounded-full -z-10 translate-x-[40%] -translate-y-[40%]"
                style={{ background: "radial-gradient(circle, #eff6ff 0%, transparent 65%)" }} />
            <div className="absolute bottom-0 left-0 w-[600px] h-[600px] rounded-full -z-10 -translate-x-[30%] translate-y-[30%]"
                style={{ background: "radial-gradient(circle, #eef2ff 0%, transparent 65%)" }} />

            <div className="max-w-7xl mx-auto relative z-10">
                <div className="flex flex-col lg:flex-row items-center gap-12 md:gap-16">
                    <motion.div
                        initial={{ opacity: 0, x: -40 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.8 }}
                        className="flex-1 lg:max-w-[46%]"
                    >
                                        <span className="inline-block px-5 py-2 rounded-full text-sm font-bold tracking-widest uppercase mb-8 border"
                            style={{ background: 'rgba(0,20,91,0.06)', color: '#0047AB', borderColor: 'rgba(0,71,171,0.15)' }}>
                            Industries We Serve
                        </span>
                        <h1 className="text-4xl md:text-5xl lg:text-6xl font-black leading-[1.1] mb-8"
                            style={{ letterSpacing: '0.04em' }}>
                            <span style={{ color: '#080400', display: 'block' }}>Digital Marketing</span>
                            {' '}
                            <span style={{ color: '#3730A3', display: 'block' }}>Tailored to Your Industry.</span>
                        </h1>
                        <p className="text-xl max-w-lg leading-relaxed font-light mb-10"
                            style={{ color: '#71717a' }}>
                            Digital strategies tailored to how each industry actually works. No two industries operate the same way.
                        </p>
                        <div className="grid grid-cols-2 gap-6 py-8 border-y" style={{ borderColor: 'rgba(0,20,91,0.1)' }}>
                            <div>
                                <h3 className="text-base font-black mb-1"
                                    style={{ color: '#00145B' }}>One Framework</h3>
                                <p className="text-sm leading-relaxed"
                                    style={{ color: '#71717a' }}>Consistent methodology across all sectors, ensuring reliability and scale.</p>
                            </div>
                            <div>
                                <h3 className="text-base font-black mb-1"
                                    style={{ color: '#00145B' }}>Custom Execution</h3>
                                <p className="text-sm leading-relaxed"
                                    style={{ color: '#71717a' }}>Tactics that adapt to compliance, competition, and unique growth goals.</p>
                            </div>
                        </div>
                    </motion.div>

                    <motion.div
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ delay: 0.3, duration: 1 }}
                        className="relative flex-1 flex items-center justify-center w-full"
                        style={{ minHeight: minH }}
                    >
                        <div className="absolute rounded-full pointer-events-none"
                            style={{ width: RX * 2, height: RY * 2, top: `calc(50% + ${OFFSET_Y}px)`, left: "50%", transform: "translate(-50%,-50%)", border: "1px dashed rgba(55,48,163,0.35)" }} />
                        <div className="absolute rounded-full pointer-events-none"
                            style={{ width: RX * 2 + 20, height: RY * 2 + 16, top: `calc(50% + ${OFFSET_Y}px)`, left: "50%", transform: "translate(-50%,-50%)", border: "1px solid rgba(55,48,163,0.15)" }} />

                        {ORBIT_INDUSTRIES.map((ind, i) => {
                            const startAngle = (360 / ORBIT_INDUSTRIES.length) * i;
                            const { x: x0, y: y0 } = pt(startAngle, RX, RY);
                            return (
                                <motion.div key={i} className="absolute z-20"
                                    style={{ top: `calc(50% + ${OFFSET_Y}px)`, left: "50%", translateX: "-50%", translateY: "-50%", x: x0, y: y0 }}
                                    animate={{ x: orbitKeyframes(startAngle, RX, RY, "x"), y: orbitKeyframes(startAngle, RX, RY, "y") }}
                                    transition={{ duration: DUR, repeat: Infinity, ease: "linear" }}
                                >
                                    <div style={{ display: "flex", alignItems: "center", gap: RX < 200 ? 5 : 8, padding: RX < 200 ? "5px 10px 5px 5px" : "8px 16px 8px 8px", borderRadius: 999, background: "rgba(255,255,255,0.85)", backdropFilter: "blur(16px)", WebkitBackdropFilter: "blur(16px)", border: `1.5px solid ${ind.color}28`, boxShadow: `0 8px 30px rgba(0,0,0,0.08), 0 0 0 1px ${ind.color}14, inset 0 1px 0 rgba(255,255,255,0.9)`, whiteSpace: "nowrap", cursor: "default", userSelect: "none" }}>
                                        <div style={{ width: RX < 200 ? 22 : 32, height: RX < 200 ? 22 : 32, borderRadius: "50%", background: `${ind.color}15`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                                            <ind.icon size={RX < 200 ? 10 : 15} color={ind.color} />
                                        </div>
                                        <span style={{ fontSize: RX < 200 ? 9 : 11, fontWeight: 800, color: "#1e293b", letterSpacing: "0.03em" }}>{ind.title}</span>
                                    </div>
                                </motion.div>
                            );
                        })}

                        <div className="relative z-10" style={{ marginTop: RX < 200 ? -40 : RX < 270 ? -60 : -112 }}>
                            <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-64 h-32 rounded-full blur-3xl -z-10" style={{ background: "radial-gradient(ellipse, rgba(37,99,235,0.08) 0%, transparent 70%)" }} />
                            <div className="relative">
                                <img
                                    src="/images/industries/industries_image.webp"
                                    alt="Lead Workz — We Scale Brands Across Industries"
                                    className="relative object-contain select-none"
                                    style={{ width: RX < 200 ? 180 : RX < 270 ? 260 : 320, filter: "drop-shadow(0 20px 40px rgba(0,0,0,0.08))" }}
                                    draggable={false}
                                />
                            </div>
                        </div>
                    </motion.div>
                </div>

                <div className="mt-20 max-w-4xl">
                    <p className="text-xl leading-relaxed italic border-l-4 pl-8"
                        style={{ color: '#00145B', borderColor: '#0047AB', opacity: 0.8 }}>
                        "We design industry-aware digital strategies — aligning design, marketing, analytics, and optimization with how your business truly functions."
                    </p>
                </div>
            </div>
        </section>
    );
}

// ─── Industries Grid ──────────────────────────────────────────────────────────

const cardIllustrations = [
    // 01 — Search: concentric circles, outer spins slow
    <svg viewBox="0 0 120 80" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full opacity-35">
        <circle cx="60" cy="40" r="35" stroke="#CEFF1A" strokeWidth="0.8" strokeDasharray="6 3" className="cf-ill-dash"/>
        <circle cx="60" cy="40" r="22" stroke="#CEFF1A" strokeWidth="1" className="cf-ill-spin"/>
        <circle cx="60" cy="40" r="10" stroke="#CEFF1A" strokeWidth="1" className="cf-ill-pulse"/>
        <circle cx="60" cy="40" r="3" fill="#CEFF1A" className="cf-ill-blink"/>
        <line x1="84" y1="58" x2="100" y2="72" stroke="#CEFF1A" strokeWidth="2" strokeLinecap="round"/>
    </svg>,
    // 02 — Layout: grid blocks float up gently
    <svg viewBox="0 0 120 80" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full opacity-35 cf-ill-float">
        <rect x="10" y="10" width="45" height="28" rx="4" stroke="#CEFF1A" strokeWidth="1"/>
        <rect x="65" y="10" width="45" height="28" rx="4" stroke="#CEFF1A" strokeWidth="1"/>
        <rect x="10" y="46" width="20" height="24" rx="4" stroke="#CEFF1A" strokeWidth="1"/>
        <rect x="38" y="46" width="72" height="24" rx="4" stroke="#CEFF1A" strokeWidth="1"/>
        <rect x="10" y="10" width="45" height="28" rx="4" fill="#CEFF1A" fillOpacity="0.06" className="cf-ill-pulse"/>
    </svg>,
    // 03 — Marketing: trend line draws itself, dots blink
    <svg viewBox="0 0 120 80" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full opacity-35">
        <polyline points="10,65 35,48 58,52 80,30 110,12" stroke="#CEFF1A" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="cf-ill-draw"/>
        <circle cx="10"  cy="65" r="3" fill="#CEFF1A" className="cf-ill-blink" style={{animationDelay:'0s'}}/>
        <circle cx="35"  cy="48" r="3" fill="#CEFF1A" className="cf-ill-blink" style={{animationDelay:'0.3s'}}/>
        <circle cx="58"  cy="52" r="3" fill="#CEFF1A" className="cf-ill-blink" style={{animationDelay:'0.6s'}}/>
        <circle cx="80"  cy="30" r="3" fill="#CEFF1A" className="cf-ill-blink" style={{animationDelay:'0.9s'}}/>
        <circle cx="110" cy="12" r="3" fill="#CEFF1A" className="cf-ill-blink" style={{animationDelay:'1.2s'}}/>
        <line x1="10" y1="70" x2="110" y2="70" stroke="#CEFF1A" strokeWidth="0.5" strokeOpacity="0.3"/>
    </svg>,
    // 04 — Optimize: outer ring pulses, inner diamond spins
    <svg viewBox="0 0 120 80" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full opacity-35">
        <circle cx="60" cy="40" r="30" stroke="#CEFF1A" strokeWidth="0.5" className="cf-ill-ring"/>
        <circle cx="60" cy="40" r="18" stroke="#CEFF1A" strokeWidth="1" className="cf-ill-pulse"/>
        <polygon points="60,22 74,40 60,58 46,40" stroke="#CEFF1A" strokeWidth="1" fill="#CEFF1A" fillOpacity="0.08" className="cf-ill-spin2"/>
        <circle cx="60" cy="40" r="4" fill="#CEFF1A" className="cf-ill-blink"/>
    </svg>,
];

const coreFocus = [
    { icon: Search, text: "Understand how customers search, decide, and convert" },
    { icon: Layout, text: "Build visually appealing, easy-to-manage digital assets" },
    { icon: TrendingUp, text: "Apply marketing strategies based on real-world business needs" },
    { icon: Zap, text: "Measure and optimize performance continuously" }
];

const industries = [
    { image: "/images/industries/ecommerce.webp",          title: "E-Commerce",          desc: "Driving growth for online stores through optimized storefronts, performance marketing, analytics, and conversion-focused strategies.", color: "from-blue-500 to-indigo-600" },
    { image: "/images/industries/saas.webp",               title: "SaaS",                desc: "Supporting SaaS brands with demand generation, product-led funnels, onboarding optimization, and data-driven performance strategies.", color: "from-indigo-500 to-purple-600" },
    { image: "/images/industries/education.webp",          title: "Education",           desc: "Helping education brands attract and convert students through structured websites, SEO, paid campaigns, and performance tracking.", color: "from-cyan-500 to-blue-600" },
    { image: "/images/industries/restaurant.webp",         title: "Restaurant",          desc: "Improving local visibility, reservations, and brand recall through local search optimization, social presence, and conversion-ready experiences.", color: "from-orange-500 to-red-600" },
    { image: "/images/industries/health-care.webp",        title: "Health Care",         desc: "Building trustworthy, patient-focused digital experiences with strong local visibility, compliant content structures, and analytics-led optimization.", color: "from-emerald-500 to-teal-600" },
    { image: "/images/industries/accounting.webp",         title: "Accounting",          desc: "Helping accounting and financial service firms establish credibility, generate qualified inquiries, and maintain a professional digital presence.", color: "from-slate-600 to-slate-800" },
    { image: "/images/industries/interior-design.webp",    title: "Interior Design",     desc: "Showcasing design expertise through visually rich websites, portfolio-led experiences, and lead-focused digital strategies.", color: "from-rose-500 to-pink-600" },
    { image: "/images/industries/real-estate.webp",        title: "Real Estate",         desc: "Supporting developers and brokers with project-focused landing pages, local and paid visibility, structured funnels, and lead tracking.", color: "from-amber-500 to-orange-600" },
    { image: "/images/industries/law-firm.webp",           title: "Law Firm",            desc: "Creating authority-driven digital platforms with SEO clarity, trust-building content, and inquiry-focused user journeys.", color: "from-stone-600 to-stone-800" },
    { image: "/images/industries/insurance.webp",          title: "Insurance",           desc: "Helping insurance brands simplify complex offerings, improve lead quality, and optimize digital acquisition with structured funnels.", color: "from-blue-600 to-cyan-600" },
    { image: "/images/industries/clothing-fashion.webp",   title: "Clothing & Fashion",  desc: "Growing fashion brands with visually engaging storefronts, performance marketing, analytics-driven optimization, and scalable growth strategies.", color: "from-fuchsia-500 to-pink-600" },
    { image: "/images/industries/hospitality-resorts.webp",title: "Hospitality & Resorts",desc: "Enhancing visibility, bookings, and brand experience for resorts and hospitality brands through immersive websites, local search, and performance marketing.", color: "from-sky-500 to-blue-600" },
];

function IndustriesGrid() {
    return (
        <section className="overflow-hidden">
            <style>{`
                @keyframes cf-spin-slow { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
                @keyframes cf-spin-rev  { from { transform: rotate(0deg); } to { transform: rotate(-360deg); } }
                @keyframes cf-pulse     { 0%,100% { opacity: 0.2; } 50% { opacity: 0.5; } }
                @keyframes cf-float-up  { 0%,100% { transform: translateY(0); } 50% { transform: translateY(-5px); } }
                @keyframes cf-dash      { to { stroke-dashoffset: -20; } }
                @keyframes cf-draw      { from { stroke-dashoffset: 220; } to { stroke-dashoffset: 0; } }
                @keyframes cf-ring-grow { 0%,100% { opacity: 0.15; transform: scale(1); } 50% { opacity: 0.4; transform: scale(1.08); } }
                @keyframes cf-blink     { 0%,100% { opacity: 1; } 50% { opacity: 0.3; } }

                .cf-ill-spin      { transform-origin: 60px 40px; animation: cf-spin-slow 10s linear infinite; }
                .cf-ill-spin2     { transform-origin: 60px 40px; animation: cf-spin-rev 14s linear infinite; }
                .cf-ill-pulse     { animation: cf-pulse 2.5s ease-in-out infinite; }
                .cf-ill-float     { animation: cf-float-up 3s ease-in-out infinite; }
                .cf-ill-dash      { stroke-dasharray: 5 4; animation: cf-dash 1.2s linear infinite; }
                .cf-ill-draw      { stroke-dasharray: 220; animation: cf-draw 2s ease-out forwards; }
                .cf-ill-ring      { transform-origin: 60px 40px; animation: cf-ring-grow 2.5s ease-in-out infinite; }
                .cf-ill-blink     { animation: cf-blink 2s ease-in-out infinite; }

.cf-card-dark { background: #1e1e1e; }
                .cf-card-dark .cf-num { color: #444; }
                .cf-card-dark .cf-iconbg { background: rgba(255,255,255,0.06); }
                .cf-card-dark .cf-icon { color: #CEFF1A; }
                .cf-card-dark .cf-text { color: #FBFDFD; }

            `}</style>
            {/* ── Core Focus — dark card block ── */}
            <div className="py-24 px-6 md:px-24" style={{ background: '#111111' }}>
                <div className="max-w-7xl mx-auto">
                    <div className="mb-14">
                        <h2 className="text-4xl md:text-5xl font-black mb-4 tracking-tight"
                            style={{ color: '#FBFDFD' }}>
                            One Framework. <span style={{ color: '#CEFF1A' }}>Sharper Intent.</span>
                        </h2>
                        <p className="text-lg leading-relaxed"
                            style={{ color: '#71717a' }}>
                            Across industries, our focus remains consistent while the execution adapts to the specific landscape.
                        </p>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
                        {coreFocus.map((item, i) => (
                            <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }}
                                className={`group flex flex-col justify-between p-8 rounded-[1.5rem] transition-all duration-300 cursor-default cf-card-dark`}
                                style={{ minHeight: 260 }}>
                                <div className="flex items-start justify-between mb-6">
                                    <span className="cf-num text-sm font-bold transition-colors duration-300"
                                        style={{ fontFamily: 'var(--font-manrope, Manrope, sans-serif)' }}>
                                        {String(i + 1).padStart(2, '0')}
                                    </span>
                                    <div className="cf-iconbg w-12 h-12 rounded-xl flex items-center justify-center transition-all duration-300">
                                        <item.icon className="cf-icon w-6 h-6 transition-colors duration-300" />
                                    </div>
                                </div>
                                <div className="mb-6 px-2">
                                    {cardIllustrations[i]}
                                </div>
                                <p className="cf-text text-base font-semibold leading-snug transition-colors duration-300"
                                    style={{ fontFamily: 'var(--font-clash), "Clash Display", sans-serif' }}>
                                    {item.text}
                                </p>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </div>

            {/* ── Industries grid ── */}
            <div className="py-32 px-6 md:px-24" style={{ background: '#111111' }}>
                <div className="max-w-7xl mx-auto">

                <div className="mb-14">
                    <h2 className="text-4xl md:text-5xl font-black mb-4 tracking-tight"
                        style={{ color: '#FBFDFD' }}>
                        Industries <span style={{ color: '#CEFF1A' }}>We Serve.</span>
                    </h2>
                    <p className="text-lg leading-relaxed"
                        style={{ color: '#71717a' }}>
                        From e-commerce to law firms, we build digital strategies tailored to how each sector actually operates.
                    </p>
                </div>

                <style>{`
                    .ind-card { position: relative; transition: transform 0.4s cubic-bezier(0.23,1,0.32,1), box-shadow 0.4s ease; }
                    .ind-card:hover { transform: translateY(-10px) scale(1.02); box-shadow: 0 30px 60px -15px rgba(0,0,0,0.4), 0 0 0 1px rgba(206,255,26,0.15); }
                    .ind-card:hover .ind-img { transform: scale(1.12); }
                    .ind-card:hover .ind-overlay { opacity: 1; }
                    .ind-card:hover .ind-title { color: #00145B; }
                    .ind-card:hover .ind-arrow { transform: translateX(6px); }
                    .ind-card:hover .ind-shine { opacity: 1; }
                    .ind-card:hover .ind-num { color: #CEFF1A; opacity: 1; }
                    .ind-img { transition: transform 0.7s cubic-bezier(0.23,1,0.32,1); }
                    .ind-overlay { opacity: 0; transition: opacity 0.5s ease; }
                    .ind-arrow { transition: transform 0.3s ease; }
                    .ind-shine { opacity: 0; transition: opacity 0.4s ease; position: absolute; inset: 0; background: linear-gradient(135deg, rgba(206,255,26,0.06) 0%, transparent 50%); pointer-events: none; border-radius: inherit; }
                    .ind-num { opacity: 0.18; transition: color 0.3s ease, opacity 0.3s ease; }
                `}</style>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 md:gap-10">
                    {industries.map((industry, i) => (
                        <motion.div
                            key={i}
                            initial={{ opacity: 0, y: 50, scale: 0.95 }}
                            whileInView={{ opacity: 1, y: 0, scale: 1 }}
                            viewport={{ once: true, margin: "-60px" }}
                            transition={{ duration: 0.55, delay: (i % 3) * 0.12, ease: [0.23, 1, 0.32, 1] }}
                            className="ind-card group relative p-8 rounded-[2rem] border flex flex-col h-full cursor-pointer"
                            style={{ background: '#ffffff', borderColor: 'rgba(0,0,0,0.08)' }}
                        >
                            {/* shine overlay */}
                            <div className="ind-shine" />

                            <div className="relative z-10 flex flex-col h-full">
                                {/* number badge */}
                                <div className="ind-num absolute top-8 right-8 text-5xl font-black leading-none select-none"
                                    style={{ color: '#00145B' }}>
                                    {String(i + 1).padStart(2, '0')}
                                </div>

                                {/* image */}
                                <div className="w-full h-48 rounded-[1.25rem] mb-7 overflow-hidden relative">
                                    <Image src={industry.image} alt={industry.title} fill className="ind-img object-cover" sizes="(max-width: 768px) 100vw, 50vw" />
                                    <div className="ind-overlay absolute inset-0 bg-gradient-to-t from-black/50 via-black/10 to-transparent" />
                                    {/* color tag appears on hover */}
                                    <div className={`absolute bottom-3 left-3 px-3 py-1 rounded-full text-white text-[10px] font-bold tracking-widest uppercase opacity-0 group-hover:opacity-100 transition-opacity duration-300 bg-gradient-to-r ${industry.color}`}
                                        style={{ fontFamily: 'var(--font-manrope, Manrope, sans-serif)' }}>
                                        {industry.title}
                                    </div>
                                </div>

                                <h3 className="ind-title text-2xl font-black mb-3 transition-colors duration-300"
                                    style={{ color: '#00145B' }}>
                                    {industry.title}
                                </h3>
                                <p className="text-sm leading-relaxed mb-8 font-medium"
                                    style={{ color: '#71717a' }}>
                                    {industry.desc}
                                </p>

                                <div className="mt-auto pt-5 border-t border-black/8 flex items-center justify-between text-xs font-bold tracking-widest uppercase"
                                    style={{ color: '#0047AB' }}>
                                    <span className="flex items-center gap-2">
                                        Engineering Growth
                                        <ArrowRight className="ind-arrow w-4 h-4" />
                                    </span>
                                    <div className={`w-2 h-2 rounded-full bg-gradient-to-br ${industry.color} opacity-60 group-hover:opacity-100 group-hover:scale-150 transition-all duration-300`} />
                                </div>
                            </div>
                        </motion.div>
                    ))}
                </div>
            </div>
            </div>
        </section>
    );
}

// ─── Industries CTA ───────────────────────────────────────────────────────────

function IndustriesCTA() {
    const { openModal } = useContactModal();

    return (
        <section className="relative py-32 md:py-48 overflow-hidden" style={{ background: '#ffffff' }}>
            <div className="absolute inset-0 z-0">
                <div className="absolute top-0 right-0 w-[800px] h-[800px] rounded-full blur-[150px] opacity-10 translate-x-1/4 -translate-y-1/2" style={{ background: '#0047AB' }} />
                <div className="absolute bottom-0 left-0 w-[600px] h-[600px] rounded-full blur-[120px] opacity-10 -translate-x-1/3 translate-y-1/3" style={{ background: '#3730A3' }} />
            </div>

            <div className="container mx-auto px-6 relative z-10">
                <div className="max-w-6xl mx-auto">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-20 items-center mb-12 pb-12 md:mb-32 md:pb-32">
                        <div>
                            <span className="inline-flex items-center gap-2 py-2 px-4 rounded-full border text-xs font-black mb-8 uppercase tracking-widest leading-none"
                                style={{ background: 'rgba(0,20,91,0.06)', borderColor: 'rgba(0,71,171,0.15)', color: '#0047AB' }}>
                                <Sparkles className="w-3.5 h-3.5" /> Support for any stage
                            </span>
                            <h2 className="text-4xl md:text-6xl font-black leading-[0.9] mb-8 uppercase"
                                style={{ color: '#080400', letterSpacing: '0.06em' }}>
                                Designed <br /><span style={{ color: '#3730A3' }}>to Scale.</span>
                            </h2>
                            <p className="text-xl font-medium leading-relaxed max-w-lg"
                                style={{ color: '#71717a' }}>
                                Whether you are launching, scaling, or expanding, we align our approach to your current reality and future goals.
                            </p>
                        </div>
                        <style>{`
                            @keyframes neon-slide { 0% { width: 2rem; opacity: 0.7; } 50% { width: 4rem; opacity: 1; } 100% { width: 2rem; opacity: 0.7; } }
                            .neon-bar { animation: neon-slide 2s ease-in-out infinite; }
                            .neon-bar-1 { animation-delay: 0s; }
                            .neon-bar-2 { animation-delay: 0.5s; }
                            .neon-bar-3 { animation-delay: 1s; }
                            .neon-bar-4 { animation-delay: 1.5s; }
                        `}</style>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
                            {["Launching a new brand", "Scaling an existing business", "Improving digital performance", "Expanding into new markets"].map((text, i) => (
                                <motion.div key={i} initial={{ opacity: 0, x: 20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }}
                                    className="p-6 rounded-2xl border"
                                    style={{ background: '#080400', borderColor: 'rgba(0,0,0,0.08)' }}>
                                    <h4 className="font-bold text-lg mb-3" style={{ color: '#FBFDFD', letterSpacing: '0.04em' }}>{text}</h4>
                                    <div className={`neon-bar neon-bar-${i + 1} h-1 rounded-full`} style={{ background: '#CEFF1A', width: '2rem' }} />
                                </motion.div>
                            ))}
                        </div>
                    </div>

                    <div className="text-center max-w-4xl mx-auto">
                        <motion.h2 initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
                            className="text-5xl md:text-8xl font-black leading-none mb-12 uppercase"
                            style={{ color: '#080400', letterSpacing: '0.03em' }}>
                            <span style={{ display: 'block' }}>Let's Build Your</span><span style={{ color: '#3730A3', display: 'block' }}>Strategy.</span>
                        </motion.h2>

                        <div className="flex flex-wrap justify-center gap-8 md:gap-12 mb-16">
                            {["Industry context awareness", "Visually striking solutions", "Business need alignment"].map((point, i) => (
                                <div key={i} className="flex items-center gap-3 font-bold tracking-tight"
                                    style={{ color: '#71717a' }}>
                                    <CheckCircle2 className="w-5 h-5" style={{ color: '#0047AB' }} />{point}
                                </div>
                            ))}
                        </div>

                        <motion.button whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}
                            onClick={openModal}
                            className="group relative px-12 py-6 rounded-full font-black text-xl md:text-2xl shadow-lg transition-all flex items-center gap-4 mx-auto"
                            style={{ background: '#080400', color: '#FFFFFF' }}>
                            Discuss Your Industry &amp; Goals
                            <ArrowRight className="w-6 h-6 group-hover:translate-x-3 transition-transform" />
                        </motion.button>
                    </div>
                </div>
            </div>

            <div className="absolute bottom-0 left-0 w-full overflow-hidden pointer-events-none select-none opacity-5 flex justify-center translate-y-1/2">
                <span className="text-[30vw] font-black uppercase whitespace-nowrap" style={{ color: '#080400' }}>LEAD WORKZ</span>
            </div>
        </section>
    );
}

// ─── Page ────────────────────────────────────────────────────────────────────

export default function IndustriesPage() {
    return (
        <main className="min-h-screen">
            <IndustriesHero />
            <IndustriesGrid />
            <IndustriesCTA />
        </main>
    );
}
